import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  updateProfile,
  EmailAuthProvider,
  linkWithCredential,
  type ActionCodeSettings,
} from 'firebase/auth';
import { validateOptionalUsername } from '../auth/displayName';
import { auth } from '../auth/firebase';
import { useFirebaseAuth } from '../auth/FirebaseAuthContext';
import {
  AuthLayout,
  AuthError,
  authInputClass,
  authButtonClass,
} from '../components/auth/AuthLayout';
import { SocialAuthButtons } from '../components/auth/SocialAuthButtons';
import { PasswordInput } from '../components/auth/PasswordInput';
import { extractFirebaseAuthError } from '../auth/firebaseErrors';
import { usePostAuthProvision } from '../auth/usePostAuthProvision';
import {
  captureFunnelEvent,
  reportGoogleAdsSignup,
} from '../services/telemetry';

const PENDING_USERNAME_KEY = 'pml.pendingUsername';
const USERNAME_SET_KEY = 'pml.usernameSet';

function readPendingUsername(): string | null {
  try {
    return sessionStorage.getItem(PENDING_USERNAME_KEY);
  } catch {
    return null;
  }
}

function writePendingUsername(name: string) {
  try {
    sessionStorage.setItem(PENDING_USERNAME_KEY, name);
  } catch {
    // Private mode: the in-memory state still covers this page load.
  }
}

function clearPendingUsername() {
  try {
    sessionStorage.removeItem(PENDING_USERNAME_KEY);
  } catch {
    // Ignore. The in-memory state is already cleared by the caller.
  }
}

function rememberUsernameSet(usernameSet: boolean) {
  try {
    // localStorage, not sessionStorage: the verification link opens another tab.
    localStorage.setItem(USERNAME_SET_KEY, usernameSet ? '1' : '0');
  } catch {
    // Private mode: this signup is recorded without the flag.
  }
}

function readUsernameSet(): boolean {
  try {
    return localStorage.getItem(USERNAME_SET_KEY) === '1';
  } catch {
    return false;
  }
}

function clearUsernameSet() {
  try {
    localStorage.removeItem(USERNAME_SET_KEY);
  } catch {
    // Ignore.
  }
}

/** The verification link sends the user to Firebase's hosted action handler,
 * which flips `emailVerified` and then returns them to the app. */
function verificationActionSettings(): ActionCodeSettings {
  return {
    url: `${window.location.origin}/sign-up`,
    handleCodeInApp: false,
  };
}

export function SignUpPage() {
  const { user } = useFirebaseAuth();
  const provisionAfterAuth = usePostAuthProvision();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [usernameError, setUsernameError] = useState<string | null>(null);
  // Set when the account exists but `updateProfile` failed. Survives a reload
  // so we don't claim a verification email was sent before the user decides.
  const [unsavedUsername, setUnsavedUsername] = useState<string | null>(() =>
    readPendingUsername()
  );
  const [pendingVerification, setPendingVerification] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const finishedVerifiedRef = useRef(false);

  useEffect(() => {
    document.title = 'Sign Up - PineappleMusicLab';
  }, []);

  const finishVerifiedSignup = useCallback(async () => {
    if (finishedVerifiedRef.current) return;
    finishedVerifiedRef.current = true;
    clearPendingUsername();
    // Cached ID token still has email_verified=false until we force-refresh.
    await auth.currentUser?.getIdToken(true);
    await provisionAfterAuth({
      userId: auth.currentUser?.uid ?? user?.uid,
      email: email || user?.email || undefined,
    });
    const usernameSet = readUsernameSet();
    clearUsernameSet();
    reportGoogleAdsSignup({ source: 'web', method: 'password' });
    captureFunnelEvent('signup', {
      source: 'web',
      method: 'password',
      usernameSet,
    });
    navigate('/workspace', { replace: true });
  }, [provisionAfterAuth, user, email, navigate]);

  // Already verified (email-link return, or they confirmed in another tab) →
  // provision Stripe now. Do not create the customer before the inbox is
  // confirmed — Firebase issues a token on createUser immediately.
  useEffect(() => {
    if (user && user.emailVerified) void finishVerifiedSignup();
  }, [user, finishVerifiedSignup]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setUsernameError(null);

    const checked = validateOptionalUsername(username);
    if (!checked.ok) {
      setUsernameError(checked.error);
      return;
    }

    setSubmitting(true);
    try {
      // Upgrade an anonymous guest in place (`linkWithCredential`) so their UID
      // — and the project they just generated as a guest — carries into the new
      // account instead of forking a second identity. Non-guests create fresh.
      const current = auth.currentUser;
      const cred = current?.isAnonymous
        ? await linkWithCredential(
            current,
            EmailAuthProvider.credential(email, password)
          )
        : await createUserWithEmailAndPassword(auth, email, password);
      // The typed username is what provisioning copies into Mongo. Stop here
      // if it cannot be saved: the account exists, but the user chooses
      // whether to retry or continue with their email as the name.
      if (checked.value) {
        try {
          await updateProfile(cred.user, { displayName: checked.value });
        } catch (profileErr) {
          writePendingUsername(checked.value);
          setUnsavedUsername(checked.value);
          setError(
            extractFirebaseAuthError(profileErr, "Couldn't save your username.")
          );
          return;
        }
      }
      clearPendingUsername();
      setUnsavedUsername(null);
      await sendVerificationEmail(checked.value !== null);
    } catch (err) {
      setError(extractFirebaseAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const sendVerificationEmail = async (usernameSet: boolean) => {
    if (!auth.currentUser) throw new Error('Not signed in');
    rememberUsernameSet(usernameSet);
    await sendEmailVerification(auth.currentUser, verificationActionSettings());
    captureFunnelEvent('signup_verification_sent', {
      source: 'web',
      method: 'password',
      usernameSet,
    });
    setPendingVerification(true);
  };

  const retryUsername = async () => {
    if (submitting || !unsavedUsername) return;
    setSubmitting(true);
    setError(null);
    try {
      if (!auth.currentUser) throw new Error('Not signed in');
      await updateProfile(auth.currentUser, { displayName: unsavedUsername });
    } catch (err) {
      setError(extractFirebaseAuthError(err, "Couldn't save your username."));
      setSubmitting(false);
      return;
    }
    clearPendingUsername();
    setUnsavedUsername(null);
    try {
      await sendVerificationEmail(true);
    } catch (err) {
      setError(extractFirebaseAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const continueWithoutUsername = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    clearPendingUsername();
    setUnsavedUsername(null);
    try {
      await sendVerificationEmail(false);
    } catch (err) {
      setError(extractFirebaseAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const resendVerification = async () => {
    setError(null);
    try {
      if (auth.currentUser) {
        await sendEmailVerification(
          auth.currentUser,
          verificationActionSettings()
        );
      }
    } catch (err) {
      setError(extractFirebaseAuthError(err));
    }
  };

  // After the user clicks the link in another tab, this re-checks the verified
  // flag and continues into the workspace.
  const continueAfterVerify = async () => {
    setError(null);
    try {
      await auth.currentUser?.reload();
      if (auth.currentUser?.emailVerified) {
        await finishVerifiedSignup();
      } else {
        setError(
          "We haven't received your verification yet. Click the link in your " +
            'email, then try again.'
        );
      }
    } catch (err) {
      setError(extractFirebaseAuthError(err));
    }
  };

  // Show the verify screen after a fresh sign-up AND whenever a signed-in user
  // still hasn't verified (e.g. routed here by the route guard or sign-in page).
  // Anonymous guests are signed in with `emailVerified === false` too, but they
  // haven't signed up yet — they must see the create-account form, not the
  // verify screen — so exclude them here.
  // `createUser` signs them in before React context catches up, so read the
  // auth singleton too. A leftover key from another visit must not replace
  // the form once nobody is signed in.
  const account = user ?? auth.currentUser;
  const showUsernameChoice =
    !!unsavedUsername &&
    !!account &&
    !account.isAnonymous &&
    !account.emailVerified;

  const awaitingVerification =
    !showUsernameChoice &&
    (pendingVerification ||
      (!!user && !user.isAnonymous && !user.emailVerified));

  if (showUsernameChoice) {
    return (
      <AuthLayout
        title="Couldn't save your username"
        subtitle="Your account was created. Try again, or continue and use your email as your name."
        showLogo={false}
      >
        <button
          className={authButtonClass}
          type="button"
          disabled={submitting}
          onClick={() => void retryUsername()}
        >
          {submitting ? 'Saving…' : 'Try again'}
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={() => void continueWithoutUsername()}
          className={`
            mt-4 w-full cursor-pointer text-sm text-gray-500
            hover:text-yellow-600
            disabled:cursor-not-allowed disabled:opacity-50
          `}
        >
          Continue without a username
        </button>
        <AuthError message={error} />
      </AuthLayout>
    );
  }

  if (awaitingVerification) {
    const verifyEmail = email || user?.email || 'your email';
    return (
      <AuthLayout
        title="Verify your email"
        subtitle={`We sent a verification link to ${verifyEmail}. Click it to activate your account. Can't find it? Check your spam or junk folder.`}
        showLogo={false}
      >
        <button
          className={authButtonClass}
          type="button"
          onClick={continueAfterVerify}
        >
          I&apos;ve verified — continue
        </button>
        <AuthError message={error} />
        <button
          onClick={resendVerification}
          className={`
            mt-5 cursor-pointer text-sm text-gray-500
            hover:text-yellow-600
          `}
        >
          Resend verification email
        </button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Create your account" showLogo={false}>
      <form onSubmit={handleCreate} className="flex flex-col gap-4">
        <input
          className={authInputClass}
          type="email"
          autoComplete="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <div>
          <input
            className={authInputClass}
            type="text"
            autoComplete="nickname"
            placeholder="Username (optional)"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              setUsernameError(null);
            }}
            aria-invalid={usernameError ? true : undefined}
          />
          {usernameError && (
            <p className="mt-1 text-sm text-red-600" role="alert">
              {usernameError}
            </p>
          )}
        </div>
        <PasswordInput
          autoComplete="new-password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />

        <button className={authButtonClass} type="submit" disabled={submitting}>
          {submitting ? 'Creating account…' : 'Sign up'}
        </button>
      </form>

      <AuthError message={error} />

      <SocialAuthButtons onError={setError} />

      <p className="mt-8 text-sm text-gray-500">
        Already have an account?{' '}
        <Link to="/sign-in" className="text-yellow-600 underline">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
